// useFileUpload.js
import * as React from 'react';
import useFetch from 'use-http';

/**
 * Reusable file upload hook that:
 * - Validates count, mime, and size
 * - Uploads to /Files (POST) and deletes /Files/:id (DELETE)
 * - Keeps caller's files state in sync (setFiles) and optionally a list of IDs (onIdsChange)
 * - Emits errors via returned state and optional notify()
 *
 * Expected backend:
 *  - POST /Files returns an array of uploaded file objects, each with a unique `fileUid`
 *  - DELETE /Files/:fileUid removes the file
 *
 * @param {Object} options
 * @param {Array}  options.files                 Current files array (objects returned by backend)
 * @param {Function} options.setFiles            Setter to update files in form + RegistrationContext
 * @param {Function} [options.onIdsChange]       Optional: receives array of file UIDs after changes
 * @param {number}   [options.maxFiles=5]        Max allowed files
 * @param {string[]} [options.acceptedMime]      Allowed mime types
 * @param {number}   [options.maxFileSize]       Max file size in bytes (default 10MB)
 * @param {boolean}  [options.isPopup=false]     If true, will try to resize large images before upload
 * @param {number}   [options.resizeThreshold]   Threshold in bytes to trigger resize (default 500KB)
 * @param {Function} [options.resizeFile]        async (file) => File (only used when isPopup)
 * @param {Function} [options.notify]            (message, type='error'|'success'|'info') => void
 */
export function useFileUpload(options = {}) {
  const {
    files = [],
    setFiles,
    onIdsChange,

    maxFiles = 5,
    acceptedMime = ['image/png', 'image/jpeg', 'application/pdf'],
    maxFileSize = 10 * 1000000, // 10 MB

    isPopup = false,
    resizeThreshold = 500000, // 500 KB
    resizeFile, // async (file) => File

    notify, // optional
  } = options;

  const { request } = useFetch('/Files');

  const [isUploading, setUploading] = React.useState(false);
  const [tooManyFilesError, setTooManyFilesError] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const [validationError, setValidationError] = React.useState(null);

  const emit = React.useCallback(
    (msg, type = 'error') => {
      if (typeof notify === 'function') notify(msg, type);
    },
    [notify]
  );

  const currentIds = React.useMemo(
    () =>
      Array.isArray(files)
        ? files.map((f) => f.fileUid).filter(Boolean)
        : [],
    [files]
  );

  const applyNext = React.useCallback(
    (nextFiles) => {
      if (typeof setFiles === 'function') setFiles(nextFiles);
      if (typeof onIdsChange === 'function') {
        onIdsChange(nextFiles.map((f) => f.fileUid).filter(Boolean));
      }
    },
    [setFiles, onIdsChange]
  );

  const resetErrors = React.useCallback(() => {
    setTooManyFilesError(false);
    setUploadError(null);
    setValidationError(null);
  }, []);

  const validateIncoming = React.useCallback(
    (incoming = []) => {
      if (!incoming || incoming.length === 0) {
        return { ok: false, reason: 'EMPTY' };
      }

      // Count check
      if (incoming.length + files.length > maxFiles) {
        return { ok: false, reason: 'TOO_MANY' };
      }

      // Type/size checks
      for (const f of incoming) {
        if (!acceptedMime.includes(f.type)) {
          return { ok: false, reason: 'MIME', file: f };
        }
        if (f.size > maxFileSize) {
          return { ok: false, reason: 'SIZE', file: f };
        }
      }

      return { ok: true };
    },
    [files.length, maxFiles, acceptedMime, maxFileSize]
  );

  const dedupe = React.useCallback(
    (incoming = []) => {
      // Pre-upload dedupe against existing by (name|size|lastModified)
      const existingKey = new Set(
        files.map((c) => {
          const name = c.name ?? c.fileName ?? '';
          const size = c.size ?? '';
          const lm = c.lastModified ?? '';
          return `${name}|${size}|${lm}`;
        })
      );
      return incoming.filter((f) => {
        const key = `${f.name}|${f.size}|${f.lastModified}`;
        return !existingKey.has(key);
      });
    },
    [files]
  );

  const preProcess = React.useCallback(
    async (incoming = []) => {
      if (!isPopup || !resizeFile) return incoming;

      const out = [];
      for (const file of incoming) {
        if (file.size >= resizeThreshold) {
          try {
            const resized = await resizeFile(file);
            out.push(resized || file);
          } catch {
            out.push(file); // fallback to original on resize failure
          }
        } else {
          out.push(file);
        }
      }
      return out;
    },
    [isPopup, resizeFile, resizeThreshold]
  );

  /**
   * Add files (File[] or FileList)
   */
  const addFiles = React.useCallback(
    async (rawList = []) => {
      resetErrors();

      const incomingRaw = Array.from(rawList || []);
      const incomingDeduped = dedupe(incomingRaw);

      const v = validateIncoming(incomingDeduped);
      if (!v.ok) {
        if (v.reason === 'TOO_MANY') {
          setTooManyFilesError(true);
          emit('Pasiekta leidžiamų failų riba.', 'error');
        } else if (v.reason === 'MIME') {
          setValidationError('Netinkamas failo formatas. Galimi: PNG, JPEG, PDF.');
          emit('Netinkamas failo formatas. Galimi: PNG, JPEG, PDF.', 'error');
        } else if (v.reason === 'SIZE') {
          setValidationError('Failas per didelis. Maksimalus dydis 10 MB.');
          emit('Failas per didelis. Maksimalus dydis 10 MB.', 'error');
        }
        return;
      }

      const incoming = await preProcess(incomingDeduped);

      const formData = new FormData();
      incoming.forEach((f) => formData.append('files', f));

      try {
        setUploading(true);
        const uploaded = await request.post(formData);
        // Expecting: uploaded is an array of file objects with fileUid
        const next = [...files, ...(Array.isArray(uploaded) ? uploaded : [])];
        applyNext(next);
        emit('Failai sėkmingai įkelti.', 'success');
      } catch (e) {
        setUploadError('Nepavyko įkelti failo. Bandykite dar kartą.');
        emit('Nepavyko įkelti failo. Bandykite dar kartą.', 'error');
      } finally {
        setUploading(false);
      }
    },
    [files, request, preProcess, validateIncoming, dedupe, applyNext, resetErrors, emit]
  );

  /**
   * Remove a file by backend UID
   */
  const removeFile = React.useCallback(
    async (fileUid) => {
      resetErrors();
      try {
        await request.delete(`/${fileUid}`);
        const next = files.filter((f) => f.fileUid !== fileUid);
        applyNext(next);
        emit('Failas pašalintas.', 'success');
      } catch (e) {
        setUploadError('Nepavyko pašalinti failo.');
        emit('Nepavyko pašalinti failo.', 'error');
      }
    },
    [files, request, applyNext, resetErrors, emit]
  );

  return {
    // actions
    addFiles,   // pass File[] or FileList
    removeFile, // pass fileUid string

    // state
    isUploading,
    tooManyFilesError,
    uploadError,
    validationError,
    currentIds,          // derived file UIDs
    remainingSlots: Math.max(0, maxFiles - files.length),

    // helpers
    resetErrors,
  };
}
